/**
 * Розділ «Адміністрування» (ADM-801): `/admin` — головна, `/admin/<id>` — сторінка.
 * Один лінивий чанк на обидві адреси; самі сторінки — окремими чанками (features/admin).
 * Сторінка тримає свій стан у параметрах адреси (`?entity=…&field=…`): глибоке посилання і «Назад».
 *
 * Чанк сторінки — маршрутний (аудит якості № 6): `loader` чекає метаданих і коду сторінки, тож
 * роутер тримає попередній екран до готовності нового (`defaultPendingMs`, app/router.tsx), а не
 * знімає його в порожнечу на час завантаження. Повторний `loader` (зміна `?entity=…`) бере все з
 * кешу й нічого не чекає.
 */
import { createRoute } from '@tanstack/react-router';
import { platformMetaQuery } from '../../platform/meta.ts';
import { ADMIN_PATH } from '../../shell/nav-model.ts';
import { rootRoute } from './root.tsx';

const adminChunk = () => import('../../features/admin/AdminSection.tsx');

export const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ADMIN_PATH,
  // Головна адмінки — з метаданих: без них розділ малював би порожнє місце.
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(platformMetaQuery()).catch(() => undefined);
  },
}).lazy(() => adminChunk().then((m) => m.AdminRoute));

/**
 * Параметри адреси сторінки адмінки: рядки до 200 символів. Роутер розбирає `?x=1` як число, тож
 * число й логічне стають рядком; об'єкти й масиви відкидаються.
 */
export type AdminSearch = { [key: string]: string | undefined };

export function adminSearch(search: Record<string, unknown>): AdminSearch {
  const out: AdminSearch = {};
  for (const [key, value] of Object.entries(search)) {
    const text =
      typeof value === 'string'
        ? value
        : typeof value === 'number' || typeof value === 'boolean'
          ? String(value)
          : null;
    if (text !== null && text.length <= 200) out[key] = text;
  }
  return out;
}

export const adminPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/$page',
  validateSearch: adminSearch,
  loader: async ({ context, params }) => {
    const section = await adminChunk();
    await section.preloadAdminPage(context.queryClient, params.page);
  },
}).lazy(() => adminChunk().then((m) => m.AdminPageRoute));
